import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ConversationsList } from "@/components/ConversationsList";
import type { Conversation } from "@/hooks/useConversations";
import { MessageSquare, RefreshCw, ChevronDown, ChevronRight, User, Globe, PanelLeftClose, PanelLeft, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";

interface AppSidebarProps {
  activeView: 'chat' | 'methods' | 'loop';
  onViewChange: (view: 'chat' | 'methods') => void;
  conversations?: Conversation[];
  currentConversationId?: string | null;
  onSelectConversation?: (id: string) => void;
  onNewConversation?: () => void;
  onDeleteConversation?: (id: string) => void;
  onRenameConversation?: (id: string, newTitle: string) => void;
  methods?: any[];
  onMethodClick?: (method: any) => void;
}


export function AppSidebar({ 
  activeView,
  onViewChange,
  conversations = [],
  currentConversationId = null,
  onSelectConversation = () => {},
  onNewConversation = () => {},
  onDeleteConversation = () => {},
  onRenameConversation = () => {},
  methods = [],
  onMethodClick = () => {},
}: AppSidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const isMobile = useIsMobile();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageOpen(false);
  };

  return (
    <Sidebar className={cn(isCollapsed ? "w-14" : "w-80")} collapsible="icon">
      {/* Toggle Button Trigger - Fixed position on the right edge */}
      <div 
        className="absolute right-0 top-20 z-50 w-6 h-12 flex items-center justify-center transition-opacity duration-200"
        style={{ transform: 'translateX(100%)' }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-12 w-6 rounded-l-none rounded-r-md bg-primary text-primary-foreground border border-l-0 shadow-md hover:bg-primary/90"
          aria-label={isCollapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
        >
          {isCollapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Modern Header */}
      <SidebarHeader className="border-b p-0 h-16">
        <div className="h-full w-full flex items-center justify-between px-4 bg-gradient-to-r from-primary to-primary/80">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary-foreground" />
              <span className="font-bold text-primary-foreground">JUNI</span>
            </div>
          )}
          {isCollapsed && (
            <RefreshCw className="h-5 w-5 text-primary-foreground mx-auto" />
          )}
        </div>
      </SidebarHeader>
      
      <SidebarContent className="gap-2">
        {/* JUNI Category with Chat */}
        {!isCollapsed && (
          <SidebarGroup className="pt-2">
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-3">
              JUNI
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {/* Chat */}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => {
                      if (isMobile) {
                        onViewChange('chat');
                      } else {
                        navigate('/chat');
                      }
                    }}
                    isActive={location.pathname === '/chat'}
                    className={cn(
                      "gap-3 py-2.5 transition-all",
                      location.pathname === '/chat' && "bg-primary text-primary-foreground font-medium shadow-sm"
                    )}
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>{t('nav.chat', 'Chat')}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Conversations List - Only show in chat view and not collapsed */}
        {!isCollapsed && activeView === 'chat' && (
          <SidebarGroup className="flex-1 overflow-hidden pt-0">
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-3">
              Konversationen
            </SidebarGroupLabel>
            <SidebarGroupContent className="flex-1 overflow-hidden">
              <ConversationsList
                conversations={conversations}
                currentConversationId={currentConversationId}
                onSelectConversation={onSelectConversation}
                onNewConversation={onNewConversation}
                onDeleteConversation={onDeleteConversation}
                onRenameConversation={onRenameConversation}
              />
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* TOOLBOX Category with Methods */}
        {!isCollapsed && methods.length > 0 && (
          <SidebarGroup className="pt-2">
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-3">
              TOOLBOX
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {methods.map((method) => (
                  <SidebarMenuItem key={method.id}>
                    <SidebarMenuButton
                      onClick={() => onMethodClick(method)}
                      className="gap-3 py-2 transition-all hover:bg-accent"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span className="text-sm truncate">{method.name}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}


        {/* Settings */}
        <SidebarGroup className="pt-2">
          <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-3">
            {!isCollapsed && t('nav.settings', 'Einstellungen')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Profile */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate('/profile')}
                  isActive={location.pathname === '/profile'}
                  className={cn(
                    "gap-3 py-2.5 transition-all",
                    location.pathname === '/profile' && "bg-primary text-primary-foreground font-medium shadow-sm"
                  )}
                >
                  <User className="h-4 w-4" />
                  {!isCollapsed && <span>{t('nav.profile', 'Profil')}</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Language Switcher */}
              {isCollapsed ? (
                <SidebarMenuItem>
                  <Popover>
                    <PopoverTrigger asChild>
                      <SidebarMenuButton
                        className={cn("gap-3 py-2.5 transition-all")}
                      >
                        <Globe className="h-4 w-4" />
                      </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent side="right" className="w-40 p-2">
                      <div className="space-y-1">
                        <button
                          onClick={() => changeLanguage('de')}
                          className={cn(
                            "w-full px-3 py-2 text-left hover:bg-accent rounded-md transition-colors flex items-center gap-2",
                            i18n.language === 'de' && "bg-accent font-medium"
                          )}
                        >
                          <span className="text-lg">🇩🇪</span>
                          <span className="text-sm">Deutsch</span>
                        </button>
                        <button
                          onClick={() => changeLanguage('en')}
                          className={cn(
                            "w-full px-3 py-2 text-left hover:bg-accent rounded-md transition-colors flex items-center gap-2",
                            i18n.language === 'en' && "bg-accent font-medium"
                          )}
                        >
                          <span className="text-lg">🇬🇧</span>
                          <span className="text-sm">English</span>
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </SidebarMenuItem>
              ) : (
                <Collapsible
                  open={isLanguageOpen}
                  onOpenChange={setIsLanguageOpen}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        className={cn(
                          "gap-3 py-2.5 transition-all"
                        )}
                      >
                        <Globe className="h-4 w-4" />
                        <span>{t('nav.language', 'Sprache')}</span>
                        <ChevronRight className={cn(
                          "h-4 w-4 ml-auto transition-transform",
                          isLanguageOpen && "rotate-90"
                        )} />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => changeLanguage('de')}
                            className={cn(
                              "text-sm py-2 hover:bg-accent/50 transition-colors",
                              i18n.language === 'de' && "bg-accent font-medium"
                            )}
                          >
                            <span className="text-lg mr-2">🇩🇪</span>
                            <span>Deutsch</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => changeLanguage('en')}
                            className={cn(
                              "text-sm py-2 hover:bg-accent/50 transition-colors",
                              i18n.language === 'en' && "bg-accent font-medium"
                            )}
                          >
                            <span className="text-lg mr-2">🇬🇧</span>
                            <span>English</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
